export const PLAN_IDS = ['monthly', '3-months', 'annual'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export const PLAN_DETAILS: Record<PlanId, { title: string; months: number; frequency: string }> = {
  monthly: { title: 'Monthly Plan', months: 1, frequency: 'month' },
  '3-months': { title: '3-Month Plan', months: 3, frequency: '3 months' },
  annual: { title: 'Annual Plan', months: 12, frequency: 'year' },
};

export interface CatalogPlan {
  id: PlanId;
  amountCents: number;
  interval: 'month' | 'year';
  intervalCount: number;
  priceId: string;
}

export interface PricingCatalog {
  catalogVersion: string;
  currency: string;
  plans: CatalogPlan[];
}

export function parsePricingCatalog(value: unknown): PricingCatalog {
  const catalog = value as PricingCatalog;
  if (
    !catalog ||
    typeof catalog.catalogVersion !== 'string' ||
    !catalog.catalogVersion.trim() ||
    typeof catalog.currency !== 'string' ||
    !/^[a-z]{3}$/i.test(catalog.currency) ||
    !Array.isArray(catalog.plans) ||
    catalog.plans.length !== PLAN_IDS.length ||
    catalog.plans.some((plan) => !plan || typeof plan !== 'object')
  ) {
    throw new Error('Subscription pricing is unavailable. Please try again.');
  }
  for (const id of PLAN_IDS) {
    const matches = catalog.plans.filter((plan) => plan.id === id);
    const plan = matches[0];
    if (
      matches.length !== 1 ||
      !Number.isSafeInteger(plan.amountCents) ||
      plan.amountCents <= 0 ||
      typeof plan.priceId !== 'string' ||
      !plan.priceId.trim() ||
      plan.interval !== (id === 'annual' ? 'year' : 'month') ||
      plan.intervalCount !== (id === '3-months' ? 3 : 1)
    ) {
      throw new Error('Subscription pricing is unavailable. Please try again.');
    }
  }
  return catalog;
}

export function formatPrice(amount: number, currency: string, locale?: string) {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

/** Use a conservative whole percentage, always relative to the same currency monthly plan. */
export function savingsPercent(amount: number, monthlyAmount: number, months: number) {
  if (months <= 1 || amount <= 0 || monthlyAmount <= 0) return 0;
  return Math.max(0, Math.floor((1 - amount / (monthlyAmount * months)) * 100));
}
